<template>
  <Teleport to="body">
    <div />
  </Teleport>
</template>

<script setup>
import { useBlokkli, onBeforeUnmount } from "#imports";
import { intersects, toShaderColor } from "#blokkli/helpers";
import onBlokkliEvent from "#blokkli/helpers/composables/onBlokkliEvent";
import vs from "./vertex.glsl?raw";
import fs from "./fragment.glsl?raw";
import {
  setBuffersAndAttributes,
  drawBufferInfo,
  setUniforms
} from "twgl.js";
import { RectangleBufferCollector } from "#blokkli/helpers/webgl";
import useDebugLogger from "#blokkli/helpers/composables/useDebugLogger";
const { eventBus, dom, theme, animation, ui } = useBlokkli();
const logger = useDebugLogger();
const props = defineProps({
  startX: { type: Number, required: true },
  startY: { type: Number, required: true },
  isPressingControl: { type: Boolean, required: true },
  gl: { type: null, required: true }
});
defineEmits(["select"]);
class MultiSelectRectangleBufferCollector extends RectangleBufferCollector {
  getBufferInfo(offset, scale) {
    const visibleBlocks = dom.getVisibleBlocks();
    const lengthBefore = this.positions.length;
    for (let i = 0; i < visibleBlocks.length; i++) {
      const uuid = visibleBlocks[i];
      if (!uuid) {
        continue;
      }
      if (this.added.has(uuid)) {
        continue;
      }
      const block = dom.findBlock(uuid);
      if (!block) {
        continue;
      }
      const el = dom.getDragElement(block);
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      const style = theme.getDraggableStyle(el);
      this.addRectangle(
        {
          id: uuid,
          x: rect.x / scale - offset.x / scale,
          y: rect.y / scale - offset.y / scale,
          width: rect.width / scale,
          height: rect.height / scale,
          isNested: block.isNested,
          radius: style.radius
        },
        block.isNested ? 6 : 5
      );
    }
    const hasChanged = lengthBefore !== this.positions.length;
    if (hasChanged) {
      this.bufferInfo = this.createBufferInfo();
    }
    return { info: this.bufferInfo, hasChanged };
  }
  getSelectedUuids(box) {
    const nested = [];
    const notNested = [];
    const rects = Object.values(this.rects);
    for (let i = 0; i < rects.length; i++) {
      const rect = rects[i];
      if (intersects(box, rect)) {
        if (rect.isNested) {
          nested.push(rect.id);
        } else {
          notNested.push(rect.id);
        }
      }
    }
    return { nested, notNested };
  }
  isSelectingNested(box) {
    const rects = Object.values(this.rects);
    for (let i = 0; i < rects.length; i++) {
      const rect = rects[i];
      if (intersects(box, rect) && rect.isNested) {
        return true;
      }
    }
    return false;
  }
}
const collector = new MultiSelectRectangleBufferCollector(props.gl);
const thick = 100;
collector.addRectangle(
  {
    width: 1e3,
    height: thick,
    x: 100,
    y: 100,
    id: "select-rect-top",
    isNested: false,
    radius: [0, 0, 0, 0]
  },
  0
);
collector.addRectangle(
  {
    width: thick,
    height: 1e3,
    x: 1e3 + thick,
    y: thick,
    id: "select-rect-right",
    isNested: false,
    radius: [0, 0, 0, 0]
  },
  0
);
collector.addRectangle(
  {
    width: 1e3,
    height: thick,
    x: 100 + thick,
    y: 1e3 + thick,
    id: "select-rect-bottom",
    isNested: false,
    radius: [0, 0, 0, 0]
  },
  0
);
collector.addRectangle(
  {
    width: thick,
    height: 1e3,
    x: 100,
    y: 100 + thick,
    id: "select-rect-left",
    isNested: false,
    radius: [0, 0, 0, 0]
  },
  0
);
const artboardOffsetStart = { ...ui.artboardOffset.value };
const artboardScaleStart = ui.artboardScale.value;
const programInfo = animation.registerProgram(
  "multi_select_overlay",
  props.gl,
  [vs, fs]
);
const uniforms = {
  u_color_field_active: toShaderColor(theme.accent.value[700]),
  u_color_field_default: toShaderColor(theme.mono.value[400]),
  u_color_area_active: toShaderColor(theme.teal.value.normal),
  u_color_area_default: toShaderColor(theme.teal.value.normal)
};
let mouseX = 0;
let mouseY = 0;
function getSelectRect(offset, scale) {
  const startX = (props.startX / artboardScaleStart + (offset.x / scale - artboardOffsetStart.x / artboardScaleStart)) * scale;
  const startY = (props.startY / artboardScaleStart + (offset.y / scale - artboardOffsetStart.y / artboardScaleStart)) * scale;
  const ax = startX > mouseX ? mouseX : startX;
  const ay = startY > mouseY ? mouseY : startY;
  const bx = startX > mouseX ? startX : mouseX;
  const by = startY > mouseY ? startY : mouseY;
  const shader = {
    x: ax,
    y: ay,
    width: bx - ax,
    height: by - ay
  };
  const check = {
    x: shader.x / scale - offset.x / scale,
    y: shader.y / scale - offset.y / scale,
    width: shader.width / scale,
    height: shader.height / scale
  };
  return { shader, check };
}
const now = Date.now();
onBlokkliEvent("canvas:draw", (e) => {
  mouseX = e.mouseX;
  mouseY = e.mouseY;
  const { shader, check } = getSelectRect(e.artboardOffset, e.artboardScale);
  const { nested } = collector.getSelectedUuids(check);
  const shouldSelectAll = props.isPressingControl || !nested.length;
  props.gl.useProgram(programInfo.program);
  const time = (Date.now() - now) / 1e3;
  setUniforms(programInfo, uniforms);
  setUniforms(programInfo, {
    u_select_all: shouldSelectAll ? 1 : 0,
    u_select_rect: [shader.x, shader.y, shader.width, shader.height],
    u_time: time
  });
  animation.setSharedUniforms(props.gl, programInfo);
  const { info, hasChanged } = collector.getBufferInfo(
    e.artboardOffset,
    e.artboardScale
  );
  if (!info) {
    return;
  }
  if (hasChanged) {
    setBuffersAndAttributes(props.gl, programInfo, info);
  }
  drawBufferInfo(props.gl, info, props.gl.TRIANGLES);
});
function getUuidsToSelect() {
  const { check } = getSelectRect(
    ui.artboardOffset.value,
    ui.artboardScale.value
  );
  const { nested, notNested } = collector.getSelectedUuids(check);
  if (props.isPressingControl) {
    return [...nested, ...notNested];
  } else if (!nested.length) {
    return notNested;
  }
  return nested;
}
onBeforeUnmount(() => {
  props.gl.clear(props.gl.COLOR_BUFFER_BIT);
  eventBus.emit("select:end", getUuidsToSelect());
  logger.log("MultiSelectOverlay unmounted");
});
</script>
